import { HLJSApi } from 'highlight.js'; // Import the highlight.js library
//import 'highlight.js/styles/github-dark.css';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { cn } from '../../../../../../libs/utils/TailwindMerge';
import { setNotifications } from '../../../../../../redux/features/app/appSlice';
import { useTheme } from '../../../../../../theme/ThemeContext';
import { copyOnClick } from '../../../../../utils/CopyUtils';
import { NOTIFICATION_TEMPLATES } from '../../../Notifications/NotificationConstants';
import { MermaidRenderer } from './MermaidRenderer';

// Define the props interface
interface CodeHighlighterProps {
    language: string;
    code: string;
}

const CodeHighlighter: React.FC<CodeHighlighterProps> = ({ language, code }) => {
    const codeRef = useRef<HTMLElement | null>(null); // Type the ref as an HTMLElement
    const [hljs, setHljs] = useState<HLJSApi | undefined>(undefined);
    const { theme } = useTheme();

    useEffect(() => {
        // Dynamically import highlight.js
        import('highlight.js').then((module) => {
            setHljs(module.default);
            if (theme === 'dark') {
                import('highlight.js/styles/github-dark.css');
            }
        });
    }, []);

    useEffect(() => {
        if (codeRef.current && typeof hljs !== 'undefined') {
            codeRef.current.dataset.highlighted = '';
            delete codeRef.current.dataset.highlighted;
            hljs.highlightElement(codeRef.current); // Apply highlight.js to the code block
        }
    }, [code, hljs]); // Re-run the highlight whenever the code changes

    return (
        <code
            ref={codeRef}
            key={code}
            className={cn(
                'min-w-full w-0 overflow-auto rounded-md border border-gray-400 dark:border-none',
                'break-words whitespace-pre-wrap',
                language,
            )}>
            {code}
        </code>
    );
};

export interface CodeRendererProps {
    className?: string; // className is optional
    children?: React.ReactNode;
}

const CodeRenderer = ({ className, children, ...props }: CodeRendererProps) => {
    const dispatch = useDispatch();
    const [messagedCopied, setMessageCopied] = useState(false);
    const { theme } = useTheme();

    // Ensure className is a string and match can be null, so we handle it safely
    const match = /language-(\w+)/.exec(className ?? '');
    const codeElementId: string = '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
        (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16),
    );

    return match && match[1] === 'mermaid' ? (
        <MermaidRenderer code={String(children).replace(/\n$/, '')} theme={theme == 'dark' ? 'dark' : 'forest'} />
    ) : match ? (
        <>
            <div className="flex justify-end mb-2">
                <button
                    title={messagedCopied ? 'Copied' : 'Copy text'}
                    type="button"
                    onClick={() => {
                        void copyOnClick(
                            `code-${codeElementId}`,
                            setMessageCopied,
                            String(children).replace(/\n$/, ''),
                        );
                        dispatch(setNotifications([{ ...NOTIFICATION_TEMPLATES.COPY_CODE }]));
                    }}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke={messagedCopied ? '#fff' : 'currentColor'}
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M8 8m0 2a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2z"></path>
                        <path d="M16 8v-2a2 2 0 0 0 -2 -2h-8a2 2 0 0 0 -2 2v8a2 2 0 0 0 2 2h2"></path>
                    </svg>
                </button>
            </div>
            <div id={`code-${codeElementId}`} className="mb-4">
                <CodeHighlighter language={match[1]} code={String(children).replace(/\n$/, '')} />
            </div>
        </>
    ) : (
        <code
            id={`code-${codeElementId}`}
            className={cn(className ?? '', 'break-words whitespace-pre-wrap')}
            {...props}>
            {children}
        </code>
    );
};

export default CodeRenderer;
